/* eslint-disable ember/no-computed-properties-in-native-classes */
import { computed, set } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import GeneralChartsOtaChart from 'lumen/components/general/charts/ota-chart';
import HotelMarketInsightChartsChartSolidgaugeLegend from 'lumen/components/hotel/market-insight/charts/chart-solidgauge-legend';
import { stackGaugeChartData } from 'lumen/utils/charts/chart-utils';
import {
  GAUGE_INNER_RADIUS,
  GAUGE_OUTER_RADIUS,
} from 'lumen/utils/constants/market-insight-constants';

const CHART_COLORS = [
  'var(--prism-color-chart-categorical-02)',
  'var(--prism-color-chart-categorical-03)',
  'var(--prism-color-chart-categorical-04)',
];

interface HotelMarketInsightChartsChartAvailabilityAlternativeLodgingSignature {
  Args: {
    onUpdateChartReference: unknown;
    summary: unknown;
  };
}

export default class HotelMarketInsightChartsChartAvailabilityAlternativeLodging extends Component<HotelMarketInsightChartsChartAvailabilityAlternativeLodgingSignature> {
  @service declare intl: Services['intl'];

  @computed(
    'args.summary.{losRestrictions,soldOutPercentageNumber,formattedAveragePrice}',
  )
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get chartData() {
    const data = [];

    const chartDataObject = {
      name: 'availability',
      className: 'gauge-chart-border',
      innerRadius: GAUGE_INNER_RADIUS,
      radius: GAUGE_OUTER_RADIUS,
    };

    if (!this.args.summary) {
      // @ts-expect-error: Incorrect type
      chartDataObject.data = [];

      return [chartDataObject];
    }

    // @ts-expect-error: Incorrect type
    const { soldOutPercentageNumber, losRestrictions } = this.args.summary;

    // Handle soldout number
    data.push({
      name: this.intl.t('market_insight.availability.closed').toString(),
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      originalY: soldOutPercentageNumber,
      color: 'var(--prism-color-chart-accent-primary-figure-default)',
    });

    // Handle LOS restriction data (if present)
    if (losRestrictions) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      losRestrictions
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        .sort(
          // @ts-expect-error: Incorrect type
          (losRestrictionA, losRestrictionB) =>
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            losRestrictionA.los - losRestrictionB.los,
        )
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        .forEach((losRestriction, index) => {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          const roundedValue = Math.round(losRestriction.value * 100);

          const losRestrictionObject = {
            originalY: roundedValue,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            color: CHART_COLORS[index],
          };

          // LOS4 is a special case. Translation should state LOS4+ eventhough there are no boundaries returned by the API
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          if (losRestriction.los === 4) {
            // @ts-expect-error: Incorrect type
            losRestrictionObject.name = `${this.intl
              .t('general.los', {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                los: losRestriction.los,
              })
              .toString()}+`;
          } else {
            // @ts-expect-error: Incorrect type
            losRestrictionObject.name = this.intl
              .t('general.los', {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                los: losRestriction.los,
              })
              .toString();
          }

          data.push(losRestrictionObject);
        });
    }

    // Use a set because the legend listens to attributes in this data
    // @ts-expect-error: Incorrect type
    set(chartDataObject, 'data', stackGaugeChartData(data));

    return [chartDataObject];
  }

  // Note: the summary we receive in this component is an alternative lodging summary!

  @computed('args.summary.formattedUnavailableHomesPercentageNumberMetricValue')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get chartOptions() {
    // @ts-expect-error: Incorrect type
    const { formattedUnavailableHomesPercentageNumberMetricValue } =
      this.args.summary ?? {};

    return {
      chart: {
        type: 'solidgauge',
        height: 90,
        spacingTop: 0,
        spacingRight: 0,
        spacingBottom: 0,
        spacingLeft: 0,
        animation: false,
      },
      colors: CHART_COLORS,
      subtitle: {
        text: `<div class="text-dark flex flex-col"><h3 class="flex justify-center">${
          formattedUnavailableHomesPercentageNumberMetricValue ?? ''
        }</h3><p class="text-200--regular" style="text-align: center;">${this.intl.t(
          'market_insight.availability.unavailable',
        )}</p></div>`,
        useHTML: true,
        align: 'center',
        verticalAlign: 'middle',
        y: 40,
      },
      title: {
        text: 'chart-mi-availability-alt-lodging',
      },
      tooltip: {
        enabled: false,
      },
      legend: {
        enabled: false,
      },
      pane: {
        center: ['50%', '100%'],
        size: '190%',
        startAngle: -90,
        endAngle: 90,
        background: [
          {
            shape: 'arc',
            innerRadius: GAUGE_INNER_RADIUS,
            outerRadius: GAUGE_OUTER_RADIUS,
            backgroundColor: 'var(--prism-color-chart-neutral-figure-empty)',
            borderWidth: 0,
          },
        ],
      },
      yAxis: {
        min: 0,
        max: 100,
        lineWidth: 0,
        tickPositions: [],
      },
      plotOptions: {
        solidgauge: {
          animation: false,
          dataLabels: {
            enabled: false,
          },
        },
      },
    };
  }

  @computed('chartData.[]')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get legendInfo() {
    if (!this.chartData.length) {
      return [];
    }

    // Data points are in reversed order for the gauge chart stacking - reverse back for the legend
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    return [...this.chartData[0].data].reverse().map((datapoint) => {
      const result = {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        name: datapoint.name,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        value: datapoint.originalY,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        color: datapoint.color,
      };

      // Only the case for available homes section
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (datapoint.formattedAveragePrice) {
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        result.averagePrice = datapoint.formattedAveragePrice;
      }

      return result;
    });
  }

  <template>
    <div class="flex flex-row items-center">
      <div class="chart-solidgauge-wrapper">
        <GeneralChartsOtaChart
          {{! @glint-expect-error: Incorrect type }}
          @chartOptions={{this.chartOptions}}
          @model={{this.chartData}}
          {{! @glint-expect-error: Incorrect type }}
          @onUpdateChartReference={{@onUpdateChartReference}}
        />
      </div>
      <HotelMarketInsightChartsChartSolidgaugeLegend
        @legendInfo={{this.legendInfo}}
      />
    </div>
  </template>
}
